import { useMemo, useState } from 'react';

import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { PrintLetterHead, useLetterHead } from '../../components/ui/PrintLetterHead';
import { ApiError } from '../../lib/api-client';
import { useChargeRates } from './rate-query.queries';

const amount = (value: number) => value.toFixed(2);
const plain = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(2));

interface DraftLine { charge: string; name: string; rate: number; quantity: number; discountPercent: number; discountAmount: number }
type DiscountType = 'flat' | 'percentage';

const lineAmount = (line: Pick<DraftLine, 'rate' | 'quantity' | 'discountPercent' | 'discountAmount'>) => {
  const gross = line.rate * line.quantity;
  return Math.max(0, gross - Math.max(line.discountAmount, gross * line.discountPercent / 100));
};

const currency = (value: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);

export const RateQueryPage = () => {
  const charges = useChargeRates();
  const letterHead = useLetterHead();
  const [chargeId, setChargeId] = useState('');
  const [draftRate, setDraftRate] = useState(0);
  const [draftQuantity, setDraftQuantity] = useState(1);
  const [draftDiscountPercent, setDraftDiscountPercent] = useState(0);
  const [draftDiscountAmount, setDraftDiscountAmount] = useState(0);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [discountType, setDiscountType] = useState<DiscountType>('flat');
  const [discountValue, setDiscountValue] = useState(0);

  const chargeOptions = useMemo(() => (charges.data ?? []).map((item) => ({ value: item.id, label: item.label })), [charges.data]);

  const resetDraft = () => { setChargeId(''); setDraftRate(0); setDraftQuantity(1); setDraftDiscountPercent(0); setDraftDiscountAmount(0); };

  const chooseCharge = (id: string) => {
    setChargeId(id);
    const selected = charges.data?.find((item) => item.id === id);
    setDraftRate(selected?.rate ?? 0);
    setDraftQuantity(1);
    setDraftDiscountPercent(0);
    setDraftDiscountAmount(0);
  };

  const draftAmount = lineAmount({ rate: draftRate, quantity: draftQuantity, discountPercent: draftDiscountPercent, discountAmount: draftDiscountAmount });

  const addCharge = () => {
    const selected = charges.data?.find((item) => item.id === chargeId);
    if (!selected || lines.some((line) => line.charge === selected.id)) return;
    setLines((current) => [...current, { charge: selected.id, name: selected.label, rate: draftRate, quantity: draftQuantity, discountPercent: draftDiscountPercent, discountAmount: draftDiscountAmount }]);
    resetDraft();
  };

  const updateLine = (index: number, patch: Partial<DraftLine>) => setLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line));
  const removeLine = (index: number) => setLines((current) => current.filter((_, lineIndex) => lineIndex !== index));

  const total = useMemo(() => lines.reduce((sum, line) => sum + lineAmount(line), 0), [lines]);
  const discountAmount = discountType === 'percentage' ? total * discountValue / 100 : discountValue;
  const grandTotal = Math.max(0, total - discountAmount);

  const clearAll = () => { resetDraft(); setLines([]); setDiscountType('flat'); setDiscountValue(0); };

  const errorMessage = charges.error instanceof ApiError ? charges.error.message : 'Charge rates could not be loaded.';

  return (
    <div className="page-stack rate-query-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Investigation</p>
          <h1>Rate Query</h1>
          <p>Look up charge rates and estimate a bill before registering a patient.</p>
        </div>
      </div>

      {charges.isLoading ? <LoadingState /> : null}
      {charges.isError ? <ErrorState title="Unable to load charges" message={errorMessage} action={{ label: 'Try again', onClick: () => void charges.refetch() }} /> : null}

      <div className="content-card rate-query-card">
        <div className="rate-query-section">
          <h2>Charges</h2>
          <div className="table-scroll">
            <table className="rate-query-table">
              <thead>
                <tr>
                  <th className="rate-query-sn">#</th>
                  <th>Charge Name<span className="rate-query-required">*</span></th>
                  <th className="rate-query-num">Rate (₹)</th>
                  <th className="rate-query-num">Qty</th>
                  <th className="rate-query-num">Dis (%)</th>
                  <th className="rate-query-num">Dis Amount (₹)</th>
                  <th className="rate-query-num">Amount (₹)</th>
                  <th className="rate-query-narrow" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                <tr className="rate-query-draft-row">
                  <td className="rate-query-sn" />
                  <td><SearchableSelect id="rate-query-charge" value={chargeId} options={chargeOptions} placeholder="Search and select a charge" onChange={chooseCharge} /></td>
                  <td><input type="number" min={0} step="0.01" value={draftRate} onChange={(event) => setDraftRate(Number(event.target.value))} aria-label="Rate" /></td>
                  <td><input type="number" min={1} value={draftQuantity} onChange={(event) => setDraftQuantity(Number(event.target.value))} aria-label="Quantity" /></td>
                  <td><input type="number" min={0} max={100} step="0.01" value={draftDiscountPercent} onChange={(event) => setDraftDiscountPercent(Number(event.target.value))} aria-label="Discount percent" /></td>
                  <td><input type="number" min={0} step="0.01" value={draftDiscountAmount} onChange={(event) => setDraftDiscountAmount(Number(event.target.value))} aria-label="Discount amount" /></td>
                  <td className="rate-query-num"><strong>{currency(draftAmount)}</strong></td>
                  <td className="rate-query-narrow"><button className="rate-query-add" type="button" onClick={addCharge} disabled={!chargeId} aria-label="Add charge" title="Add charge">+</button></td>
                </tr>
                {lines.length === 0 ? <tr><td className="rate-query-empty-row" colSpan={8}>No charges added yet. Select a charge above and press +.</td></tr> : null}
                {lines.map((line, index) => (
                  <tr key={line.charge}>
                    <td className="rate-query-sn">{index + 1}</td>
                    <td className="rate-query-name">{line.name}</td>
                    <td><input type="number" min={0} step="0.01" value={line.rate} onChange={(event) => updateLine(index, { rate: Number(event.target.value) })} aria-label={`Rate for ${line.name}`} /></td>
                    <td><input type="number" min={1} value={line.quantity} onChange={(event) => updateLine(index, { quantity: Number(event.target.value) })} aria-label={`Quantity for ${line.name}`} /></td>
                    <td><input type="number" min={0} max={100} step="0.01" value={line.discountPercent} onChange={(event) => updateLine(index, { discountPercent: Number(event.target.value) })} aria-label={`Discount percent for ${line.name}`} /></td>
                    <td><input type="number" min={0} step="0.01" value={line.discountAmount} onChange={(event) => updateLine(index, { discountAmount: Number(event.target.value) })} aria-label={`Discount amount for ${line.name}`} /></td>
                    <td className="rate-query-num"><strong>{currency(lineAmount(line))}</strong></td>
                    <td className="rate-query-narrow"><button className="rate-query-remove" type="button" onClick={() => removeLine(index)} aria-label={`Remove ${line.name}`} title="Remove">×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rate-query-summary">
            <div className="rate-query-summary-row"><span>Total</span><strong>{currency(total)}</strong></div>
            <div className="rate-query-summary-row">
              <span>Discount</span>
              <div className="rate-query-summary-discount">
                <input type="number" min={0} step="0.01" value={discountValue} onChange={(event) => setDiscountValue(Number(event.target.value))} aria-label="Bill discount" />
                <select value={discountType} onChange={(event) => setDiscountType(event.target.value as DiscountType)} aria-label="Discount type">
                  <option value="flat">₹</option>
                  <option value="percentage">%</option>
                </select>
              </div>
            </div>
            <div className="rate-query-summary-row rate-query-summary-grand"><span>Grand Total</span><strong>{currency(grandTotal)}</strong></div>
          </div>
        </div>

        <div className="rate-query-form-actions">
          <button className="button button-secondary" type="button" onClick={clearAll}>Clear</button>
          <button className="button button-primary" type="button" onClick={() => window.print()} disabled={lines.length === 0 || !letterHead.isReady}>Print</button>
        </div>
      </div>

      {/* Print-only estimate: the on-screen form is hidden when printing. */}
      <div className="rate-query-print" aria-hidden="true">
        <PrintLetterHead imageUrl={letterHead.imageUrl} />
        <table className="rate-query-print-table">
          <thead>
            <tr>
              <th className="rate-query-print-sn">SL.NO</th>
              <th>PARTICULARS</th>
              <th className="rate-query-print-num">RATE</th>
              <th className="rate-query-print-num">QTY</th>
              <th className="rate-query-print-num">DISCOUNT</th>
              <th className="rate-query-print-num">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const gross = line.rate * line.quantity;
              const lineDiscount = gross - lineAmount(line);
              return (
                <tr key={line.charge}>
                  <td className="rate-query-print-sn">{index + 1}</td>
                  <td>{line.name}</td>
                  <td className="rate-query-print-num">{plain(line.rate)}</td>
                  <td className="rate-query-print-num">{line.quantity}</td>
                  <td className="rate-query-print-num">{plain(lineDiscount)}</td>
                  <td className="rate-query-print-num">{amount(lineAmount(line))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="rate-query-print-totals">
          <p><span>Net Amount</span><span>{amount(total)}</span></p>
          <p><span>Discount</span><span>{plain(discountValue)} {discountType === 'percentage' ? '%' : 'Rs'}</span></p>
          <p className="rate-query-print-grand"><span>Total Amount</span><span>{amount(grandTotal)}</span></p>
        </div>
      </div>
    </div>
  );
};
